import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import fileDownload from 'js-file-download';
import Toast from '../components/Toast';
import { jsonDelete } from '../api/jsonRequest';
import { getAuthConfig } from '../mixins/ApiConfig';
import { queryKeys } from '../query/queryKeys';
import { buildPollUrl } from '../query/queryParams';
import { getUploadErrorMessage } from '../utils/uploadLimits';
import { usePollQuery } from '../query/usePollQuery';

function uploadErrorToast(error, fallbackTitle) {
    const sizeMessage = getUploadErrorMessage(error);
    Toast.fire({
        title: sizeMessage || fallbackTitle,
        icon: 'error',
    });
}

export function useCustomerSurveys({ companyId, enabled = true, pauseRefetch = false }) {
    const url = buildPollUrl('/api/SurveyFile/FetchSurveyFilesForCustomer', companyId);

    return usePollQuery({
        queryKey: queryKeys.surveys.customer(companyId),
        url,
        enabled: Boolean(companyId) && enabled,
        pauseRefetch,
    });
}

export function useProcessorSurveys({ companyId, enabled = true, pauseRefetch = false }) {
    const url = buildPollUrl('/api/SurveyFile/FetchSurveyFilesForProcessor', companyId);

    return usePollQuery({
        queryKey: queryKeys.surveys.processor(companyId),
        url,
        enabled: Boolean(companyId) && enabled,
        pauseRefetch,
    });
}

export function useDeleteSurveyFiles() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (fileIds) =>
            jsonDelete(`/api/SurveyFile/DeleteSurveyFile/${JSON.stringify(fileIds)}`),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: queryKeys.surveys.all() });
        },
    });
}

export function useUploadSurveyFile() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({
            file,
            ProcessorCompanyId,
            CustomerCompanyId,
            CustomerUploaderId,
        }) => {
            const config = getAuthConfig();
            const fileUploadConfig = { ...config };
            delete fileUploadConfig.headers['Content-Type'];
            const formData = new FormData();
            formData.append('file', file);
            formData.append('ProcessorCompanyId', ProcessorCompanyId);
            formData.append('CustomerCompanyId', CustomerCompanyId);
            formData.append('CustomerUploaderId', CustomerUploaderId);
            const res = await axios.post(
                '/api/SurveyFile/SurveyFileUpload',
                formData,
                fileUploadConfig,
            );
            if (res.status != 200) {
                throw new Error('Upload failed');
            }
            return res.data;
        },
        onSuccess: () => {
            Toast.fire({ title: 'File Uploaded', icon: 'success' });
            void queryClient.invalidateQueries({ queryKey: queryKeys.surveys.all() });
        },
        onError: (error) => {
            uploadErrorToast(error, 'Error with Uploading. Please try again.');
        },
    });
}

export function useDownloadSurveyFile() {
    return useMutation({
        mutationFn: async ({ fileId, name }) => {
            const config = getAuthConfig();
            const res = await axios.get(`/api/SurveyFile/DownloadSurveyFile/${fileId}`, config);
            fileDownload(res.data, `${name}`);
        },
    });
}
